import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { motionFadeIn } from '@apps-common/config/motion-variants'
import { imageUrlReplace } from '@apps-common/helpers/url-replace'
import { AnimatePresence, motion } from 'framer-motion'

interface IProps {
  keyPrefix: string
  src: string | undefined
  alt: string
  imageClasses: string
  containerClasses?: string
  Fallback: any
  Loader: any
  isLoadingOverride?: boolean
}

export const ImageWithFallback: React.FC<IProps> = ({
  keyPrefix,
  src,
  alt,
  imageClasses,
  containerClasses,
  Fallback,
  Loader,
  isLoadingOverride
}) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [brokenImg, setBrokenImg] = useState<boolean>(false)
  const [resolvedSrc, setResolvedSrc] = useState<string>()

  useEffect(() => {
    setLoading(true)
    setBrokenImg(false)
    const updatedSrc = imageUrlReplace(src)
    setResolvedSrc(updatedSrc)
  }, [src])

  useEffect(() => {
    if (!resolvedSrc) {
      setLoading(false)
      setBrokenImg(true)
      return
    }

    let cancelled = false
    const preloadImage = new window.Image()

    setLoading(true)
    setBrokenImg(false)

    preloadImage.onload = () => {
      if (!cancelled) {
        setLoading(false)
      }
    }

    preloadImage.onerror = () => {
      if (!cancelled) {
        setLoading(false)
        setBrokenImg(true)
      }
    }

    preloadImage.src = resolvedSrc

    if (preloadImage.complete) {
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [resolvedSrc])

  const imageLoading = isLoadingOverride || loading

  return (
    <AnimatePresence mode='wait'>
      {imageLoading && (
        <motion.div key={`${keyPrefix}-loader`} {...motionFadeIn(0.2)} className='w-full h-full'>
          {Loader}
        </motion.div>
      )}
      {!imageLoading && !brokenImg && (
        <>
          {!!containerClasses && (
            <motion.div key={`${keyPrefix}-image-with-container`} {...motionFadeIn(0.2)} className={containerClasses}>
              <img src={resolvedSrc} alt={alt} className={classNames(imageClasses)} />
            </motion.div>
          )}
          {!containerClasses && (
            <motion.img
              key={`${keyPrefix}-image-no-container`}
              {...motionFadeIn(0.2)}
              src={resolvedSrc}
              alt={alt}
              className={classNames(imageClasses)}
            />
          )}
        </>
      )}
      {!imageLoading && brokenImg && (
        <motion.div key={`${keyPrefix}-fallback`} {...motionFadeIn(0.2)} className='w-full h-full'>
          {Fallback}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
